import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { ChevronDown, ChevronUp, HelpCircle, Phone, Mail, MessageCircle, ArrowLeft } from 'lucide-react';

const FAQ_CATEGORIES = [
  {
    title: 'Informazioni Generali',
    icon: '📋',
    questions: [
      {
        q: 'Cosa sono gli abbonamenti Prime Vision?',
        a: 'Gli abbonamenti Prime Vision sono piani di assistenza oculistica continuativa che ti permettono di accedere a visite, esami diagnostici e altri servizi a un costo fisso mensile. Sono pensati per garantirti continuità di cura, risparmio nel tempo e accesso prioritario alle prestazioni.'
      },
      {
        q: 'Quali piani di abbonamento sono disponibili?',
        a: 'Offriamo due piani: Base (€59/mese individuale, €119/mese famiglia) e Premium (€99/mese individuale, €199/mese famiglia). Ogni piano include servizi diversi per adattarsi alle tue esigenze. Gli abbonamenti possono essere singoli o familiari (fino a 5 persone dello stesso nucleo familiare).'
      },
      {
        q: 'Posso provare un abbonamento prima di impegnarmi?',
        a: 'Sì, offriamo un periodo di prova di 30 giorni con possibilità di recesso senza penali. Durante questo periodo puoi valutare se il piano scelto è adatto alle tue esigenze.'
      },
      {
        q: 'Gli abbonamenti sono adatti a tutti?',
        a: 'Sì, i nostri abbonamenti sono pensati per persone di tutte le età. In particolare, consigliamo il piano Premium a chi ha bisogno di controlli frequenti o ha condizioni oculari croniche (glaucoma, diabete, maculopatia).'
      },
      {
        q: 'Quali sono gli orari del centro?',
        a: 'Siamo aperti dal Lunedì al Venerdì dalle 8:30 alle 19:30. Inoltre, siamo aperti 2 Sabati al mese dalle 9:30 alle 12:30.'
      }
    ]
  },
  {
    title: 'Piani Famiglia',
    icon: '👨‍👩‍👧‍👦',
    questions: [
      {
        q: 'Quante persone possono essere incluse nel piano famiglia?',
        a: 'Il piano famiglia include fino a 5 membri dello stesso nucleo familiare. Possono essere inclusi coniugi, figli, genitori conviventi o altri familiari.'
      },
      {
        q: 'I membri della famiglia devono avere lo stesso cognome?',
        a: 'No, non è necessario avere lo stesso cognome. È sufficiente che i membri siano parte dello stesso nucleo familiare (conviventi o parenti di primo/secondo grado).'
      },
      {
        q: 'Posso aggiungere o rimuovere membri dalla famiglia?',
        a: 'Sì, puoi modificare i membri del tuo piano famiglia in qualsiasi momento, fino a un massimo di 5 persone. Le modifiche saranno effettive dal mese successivo.'
      },
      {
        q: 'Ogni membro della famiglia può prenotare autonomamente?',
        a: 'Sì, ogni membro adulto del piano famiglia ha accesso autonomo al sistema di prenotazione e può gestire i propri appuntamenti. Per i minori, le prenotazioni vengono gestite dal titolare del piano.'
      },
      {
        q: 'Qual è il risparmio con un piano famiglia?',
        a: 'Con il piano famiglia risparmi rispetto a sottoscrivere abbonamenti individuali per ogni membro. Ad esempio, il piano Base famiglia (€119/mese per 5 persone) costa meno di 2 abbonamenti individuali (€59 x 2 = €118).'
      }
    ]
  },
  {
    title: 'Visite e Prestazioni',
    icon: '👁️',
    questions: [
      {
        q: 'Cosa include la visita oculistica completa?',
        a: 'La visita oculistica completa include: misurazione della vista, esame del fundus oculi, misurazione della pressione oculare, esame con lampada a fessura. Con il piano Premium sono inclusi anche OCT (tomografia a coerenza ottica) e altri esami diagnostici avanzati.'
      },
      {
        q: 'Quante visite sono incluse in ogni piano?',
        a: 'Il piano Base include 2 visite all\'anno. Il piano Premium include visite illimitate durante l\'anno, senza costi aggiuntivi.'
      },
      {
        q: 'Gli esami diagnostici sono tutti inclusi?',
        a: 'Dipende dal piano: il Base include esami di base (visus, pressione, fundus). Il Premium include anche OCT, campo visivo, topografia corneale e pachimetria.'
      },
      {
        q: 'Come funziona la telemedicina?',
        a: 'La telemedicina ti permette di effettuare videoconsulti con i nostri specialisti comodamente da casa. È inclusa nel piano Premium. I videoconsulti sono disponibili dal Lunedì al Venerdì, 12:30-13:00, con slot da 10 minuti.'
      }
    ]
  },
  {
    title: 'Chirurgia e Trattamenti',
    icon: '⚕️',
    questions: [
      {
        q: 'Cosa include il piano Premium in termini di chirurgia?',
        a: 'Il piano Premium include un intervento chirurgico oculistico all\'anno (es. cataratta, strabismo, pterigio) se clinicamente indicato. L\'intervento viene valutato dai nostri medici in base alle tue necessità e include anche il follow-up post-operatorio.'
      },
      {
        q: 'Le iniezioni intravitreali sono incluse?',
        a: 'Sì, con il piano Premium sono incluse fino a 3 iniezioni intravitreali (IVT) all\'anno, fondamentali per il trattamento di maculopatie e altre patologie retiniche.'
      },
      {
        q: 'Posso scegliere il chirurgo?',
        a: 'Sì, con il piano Premium puoi esprimere preferenze sul chirurgo. Compatibilmente con le disponibilità, cercheremo di accontentare le tue richieste.'
      }
    ]
  },
  {
    title: 'Servizi Premium',
    icon: '⭐',
    questions: [
      {
        q: 'Cosa include il servizio autista?',
        a: 'Con il piano Premium hai diritto al servizio di accompagnamento gratuito. Un autista ti viene a prendere a casa e ti riaccompagna dopo la visita o l\'intervento. Puoi richiederlo al momento della prenotazione.'
      },
      {
        q: 'Come funziona la prenotazione prioritaria?',
        a: 'Con il piano Premium garantiamo che potrai prenotare una visita entro 24-48 ore dalla richiesta. Questo ti evita le lunghe liste d\'attesa.'
      },
      {
        q: 'Posso contattare direttamente il medico?',
        a: 'Sì, con il piano Premium puoi comunicare direttamente con il tuo medico tramite la chat interna del sito o dell\'app, e programmare videoconsulti.'
      }
    ]
  },
  {
    title: 'Prenotazioni e Disdette',
    icon: '📅',
    questions: [
      {
        q: 'Come posso prenotare un appuntamento?',
        a: 'Puoi prenotare online tramite il sito o l\'app, oppure telefonicamente. Gli abbonati Premium hanno accesso a slot prioritari.'
      },
      {
        q: 'Posso disdire o spostare un appuntamento?',
        a: 'Sì, puoi spostare o disdire il tuo appuntamento in qualsiasi momento, anche poco prima. Ti chiediamo solo di avvisarci il prima possibile per permetterci di offrire lo slot ad altri pazienti.'
      },
      {
        q: 'Come funziona il check-in in studio?',
        a: 'Quando arrivi in studio, scannerizza il QR code della tua Patient Card. Se sei abbonato Premium, verrai automaticamente registrato in sala d\'attesa. Se hai prestazioni a pagamento, riceverai un link di pagamento via SMS e email.'
      }
    ]
  },
  {
    title: 'Pagamenti e Abbonamento',
    icon: '💳',
    questions: [
      {
        q: 'Come posso pagare l\'abbonamento?',
        a: 'Accettiamo pagamenti con carta di credito/debito tramite il nostro sistema sicuro. L\'addebito avviene mensilmente in automatico.'
      },
      {
        q: 'Posso disdire l\'abbonamento in qualsiasi momento?',
        a: 'Sì, puoi disdire l\'abbonamento quando vuoi. La disdetta sarà effettiva dalla fine del mese in corso. Non ci sono penali di uscita.'
      },
      {
        q: 'Posso cambiare piano durante l\'abbonamento?',
        a: 'Sì, puoi passare da Base a Premium (upgrade) o viceversa (downgrade) in qualsiasi momento. La modifica sarà effettiva dal mese successivo.'
      },
      {
        q: 'Ci sono costi nascosti?',
        a: 'No, i prezzi indicati sono tutto incluso. Non ci sono costi di attivazione, costi nascosti o penali di uscita.'
      }
    ]
  }
];

export const FAQAbbonamenti = () => {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredCategories = FAQ_CATEGORIES.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
           q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen" style={{ background: '#F8F8F8' }}>
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 px-4" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <button
            onClick={() => navigate('/abbonamenti')}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna agli abbonamenti
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Domande Frequenti
          </h1>
          <p className="text-lg text-white/90" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Trova le risposte alle domande più comuni sui nostri abbonamenti
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 px-4 -mt-6">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <HelpCircle className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca nelle FAQ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 shadow-lg focus:outline-none focus:ring-2"
              style={{ borderColor: '#E5E7EB', background: 'white' }}
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-8 px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessun risultato trovato per "{searchTerm}"</p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredCategories.map((category, catIndex) => (
                <div key={catIndex} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b" style={{ borderColor: '#E5E7EB', background: 'rgba(127, 179, 160, 0.05)' }}>
                    <h2 className="text-xl font-bold flex items-center gap-3" style={{ color: '#2A9FCB' }}>
                      <span className="text-2xl">{category.icon}</span>
                      {category.title}
                    </h2>
                  </div>
                  
                  <div className="divide-y" style={{ borderColor: '#E5E7EB' }}>
                    {category.questions.map((item, qIndex) => {
                      const isExpanded = expandedItems[`${catIndex}-${qIndex}`];
                      return (
                        <div key={qIndex} className="px-6">
                          <button
                            onClick={() => toggleItem(catIndex, qIndex)}
                            className="w-full py-4 flex items-start justify-between text-left"
                          >
                            <span className="font-medium pr-4" style={{ color: '#333' }}>
                              {item.q}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#2A9FCB' }} />
                            ) : (
                              <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#2A9FCB' }} />
                            )}
                          </button>
                          
                          {isExpanded && (
                            <div className="pb-4 text-gray-600 leading-relaxed">
                              {item.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 px-4" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Non hai trovato la risposta?
          </h2>
          <p className="text-white/90 mb-8">
            Il nostro team è a tua disposizione per rispondere a qualsiasi domanda
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+390810000000"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl font-medium transition-transform hover:scale-105"
              style={{ color: '#2A9FCB' }}
            >
              <Phone className="w-5 h-5" />
              Chiamaci
            </a>
            <a
              href="mailto:info@primevision.it"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl font-medium transition-transform hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              Scrivici
            </a>
            <button
              onClick={() => {
                // Open chat widget
                const chatButton = document.querySelector('[data-chat-widget]');
                if (chatButton) chatButton.click();
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 text-white rounded-xl font-medium transition-transform hover:scale-105"
            >
              <MessageCircle className="w-5 h-5" />
              Chat
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQAbbonamenti;
